import {
  diasAteEscritorio,
  hojeEscritorio,
} from 'dashboard/helper/fusoEscritorio';

/**
 * Cor de cada tipo de atividade, em UM lugar só.
 *
 * Vive fora dos componentes porque o calendário (card e legenda) e o
 * formulário (faixa do cabeçalho) precisam da MESMA cor. Copiar o valor no
 * segundo lugar é assinar para os dois divergirem no primeiro ajuste — e
 * legenda que diverge do que está na tela é pior que legenda nenhuma, regra
 * que este módulo já seguia internamente.
 */
export const CORES_POR_TIPO = {
  commitment: 'rgb(var(--blue-9))',
  task: 'rgb(var(--iris-9))',
};

/** Cor do tipo, com o compromisso como padrão para tipo desconhecido. */
export const corDoTipo = tipo =>
  CORES_POR_TIPO[tipo] || CORES_POR_TIPO.commitment;

/**
 * A mesma cor em tokens da paleta, para quem pinta fundo/texto/borda em vez
 * de um ponto sólido.
 *
 * Existe porque a cópia aconteceu exatamente como o comentário acima previa:
 * em 04/09/2026, "tarefa" tinha QUATRO cores diferentes no produto —
 * #d97706 (aqui, no calendário), #8b5cf6 no AgendaDashboard, teal no
 * AgendaActivitiesList e iris no AgendaKanbanView. Cada tela copiou um valor
 * em vez de importar este arquivo.
 *
 * A agenda é uma só no sistema inteiro: se o mesmo compromisso muda de cor
 * conforme a tela, a cor deixa de identificar e vira enfeite.
 */
/**
 * O mesmo tipo, como TOM do selo do produto (`.ficha-pill--*`).
 *
 * Existe pelo mesmo motivo do mapa acima, e a cópia aconteceu de novo: em
 * 12/09/2026 o Dashboard do Jurídico tinha um mapa PRÓPRIO em que "tarefa"
 * era TEAL — na mesma tela em que o mapa de baixo a pintava de ÂMBAR. O
 * mesmo compromisso mudava de cor entre a lista de vencidas e a de hoje.
 */
export const SELO_POR_TIPO = {
  commitment: 'ficha-pill--azul',
  task: 'ficha-pill--iris',
};

/** O tom do selo para o tipo, com compromisso como padrão. */
export const seloDoTipo = tipo =>
  SELO_POR_TIPO[tipo] || SELO_POR_TIPO.commitment;

export const TOKENS_POR_TIPO = {
  commitment: {
    bg: 'rgb(var(--blue-3))',
    text: 'rgb(var(--blue-11))',
    border: 'rgb(var(--blue-6))',
  },
  task: {
    bg: 'rgb(var(--iris-3))',
    text: 'rgb(var(--iris-11))',
    border: 'rgb(var(--iris-6))',
  },
};

/** Tokens do tipo, com o compromisso como padrão para tipo desconhecido. */
export const tokensDoTipo = tipo =>
  TOKENS_POR_TIPO[tipo] || TOKENS_POR_TIPO.commitment;

/**
 * Selo por STATUS da atividade, em classes do vocabulário compartilhado.
 *
 * Mora aqui pelo mesmo motivo das cores de tipo: o status de um compromisso
 * aparece na agenda, na ficha do processo e na ficha do cliente. Na ficha do
 * cliente ele estava escrito em sete cores hexadecimais cravadas — `#fef3c7`,
 * `#d1fae5`, `#fee2e2` e companhia —, nenhuma delas da paleta do produto.
 *
 * A escala responde "em que pé está": o que espera é âmbar, o que corre é
 * azul, o que começou é iris, o que terminou bem é teal, o recusado é rubi e
 * o cancelado é neutro.
 */
export const TOM_STATUS_ATIVIDADE = {
  pending: 'ficha-pill--ambar',
  in_progress: 'ficha-pill--azul',
  started: 'ficha-pill--iris',
  completed: 'ficha-pill--ok',
  cancelled: 'ficha-pill--neutro',
  refused: 'ficha-pill--alerta',
  not_completed: 'ficha-pill--ambar',
};

/** Selo do status, neutro para status desconhecido. */
export const tomDoStatusAtividade = status =>
  TOM_STATUS_ATIVIDADE[status] || 'ficha-pill--neutro';

/**
 * A COR de cada status, em token — para o ponto do seletor, a bolinha do card
 * e a legenda. Estava copiada, com os mesmos sete valores, no calendário e no
 * detalhe da atividade, e a ficha rápida ia virar a terceira cópia.
 */
export const COR_DO_STATUS_ATIVIDADE = {
  pending: 'rgb(var(--amber-9))',
  in_progress: 'rgb(var(--blue-9))',
  started: 'rgb(var(--iris-9))',
  completed: 'rgb(var(--teal-9))',
  cancelled: 'rgb(var(--slate-8))',
  refused: 'rgb(var(--ruby-9))',
  not_completed: 'rgb(var(--amber-9))',
};

/** A cor do status, neutra para status desconhecido. */
export const corDoStatusAtividade = status =>
  COR_DO_STATUS_ATIVIDADE[status] || 'rgb(var(--slate-8))';

/**
 * O TOM de gráfico do status — barra, fatia, ponto (`.advup-tom--*`). A mesma
 * família do selo: o que espera é âmbar, o que corre é azul, o que começou é
 * iris, o que terminou bem é teal, o recusado é rubi, o cancelado é neutro.
 * O Painel tinha um mapa próprio de selo para tom; os Relatórios iam copiar.
 */
export const TOM_DE_GRAFICO_DO_STATUS = {
  pending: 'ambar',
  in_progress: 'azul',
  started: 'iris',
  completed: 'teal',
  cancelled: 'neutro',
  refused: 'rubi',
  not_completed: 'ambar',
};

/** O tom de gráfico do status, neutro para status desconhecido. */
export const tomDeGraficoDoStatus = status =>
  TOM_DE_GRAFICO_DO_STATUS[status] || 'neutro';

/**
 * Selo por TIPO, na mesma linguagem de classe do status — para quem usa
 * `.ficha-pill` em vez de pintar fundo/texto/borda por estilo.
 */
export const tomDoTipoAtividade = tipo =>
  tipo === 'task' ? 'ficha-pill--iris' : 'ficha-pill--azul';

/**
 * O tom da FAMÍLIA (`advup-ativ--*`, `advup-tom--*`) de cada tipo.
 *
 * Victor, 14/09/2026: "tarefa roxo, prazo fatal vermelho, compromisso continua
 * azul e o âmbar pode ser algo de atenção". O âmbar deixou de ser tipo e virou
 * o sinal de ATRASADA (`estaAtrasada`); o rubi é só do prazo fatal.
 */
export const TOM_DO_TIPO = { commitment: 'azul', task: 'iris' };

export const tomDoTipo = tipo => TOM_DO_TIPO[tipo] || TOM_DO_TIPO.commitment;

const STATUS_ENCERRADOS = ['completed', 'cancelled', 'refused'];

/** Cumprida, cancelada ou recusada: não pede mais nada de ninguém. */
export const estaEncerrada = atividade =>
  STATUS_ENCERRADOS.includes(atividade.legal_status);

/** Passou do horário (fim, ou início quando não há fim) e ninguém encerrou. */
export const estaAtrasada = (atividade, agora = new Date()) => {
  if (estaEncerrada(atividade)) return false;
  const limite = atividade.end_at || atividade.start_at;
  return Boolean(limite) && new Date(limite) < agora;
};

/**
 * Chegou o dia do prazo fatal (ou passou), e ninguém encerrou. Contado no
 * calendário do ESCRITÓRIO.
 */
export const prazoFatalChegou = (atividade, agora = new Date()) =>
  Boolean(atividade.fatal_deadline_at) &&
  !estaEncerrada(atividade) &&
  hojeEscritorio(new Date(atividade.fatal_deadline_at)) <=
    hojeEscritorio(agora);

/**
 * O TOM de um card de atividade (`.advup-ativ--*`), o mesmo em toda tela:
 * prazo fatal chegado > cumprida > atrasada > tipo. Morava só no calendário;
 * a ficha do cliente precisou do mesmo desempate.
 */
export const tomDaAtividade = (atividade, agora = new Date()) => {
  if (prazoFatalChegou(atividade, agora)) return 'rubi';
  if (atividade.legal_status === 'completed') return 'teal';
  if (estaAtrasada(atividade, agora)) return 'ambar';
  return tomDoTipo(atividade.activity_type);
};

/**
 * O ícone de cada tipo de vínculo. Morava na ficha da atividade; o formulário
 * precisou do mesmo, e o vínculo tem que ter a mesma cara nos dois.
 */
export const ICONE_DO_VINCULO = {
  LegalProcess: 'i-ph-scales-fill',
  Contact: 'i-ph-user-fill',
  Conversation: 'i-ph-chat-circle-text-fill',
  Contract: 'i-ph-file-text-fill',
  Publication: 'i-ph-newspaper-fill',
  Other: 'i-ph-link-bold',
};

/**
 * O ícone de cada papel de envolvido: no formulário a ficha que acende, na
 * ficha da atividade o selo ao lado do nome.
 */
export const ICONE_DO_PAPEL = {
  role_requester: 'i-ph-megaphone-simple',
  role_responsible: 'i-ph-star',
  role_executor: 'i-ph-wrench',
  role_area_responsible: 'i-ph-buildings',
  role_immediate_superior: 'i-ph-crown-simple',
};

/**
 * O tom do ladrilho de cada vínculo — a mesma semântica do resto do produto:
 * processo é azul, contato teal, conversa íris, contrato âmbar.
 */
export const TOM_DO_VINCULO = {
  LegalProcess: 'azul',
  Contact: 'teal',
  Conversation: 'iris',
  Contract: 'ambar',
  Publication: 'rubi',
  Other: 'neutro',
};

/** Usuário do escritório ou contato: o ícone ao lado do tipo. */
export const ICONE_DO_ENVOLVIDO = {
  User: 'i-ph-user-circle',
  Contact: 'i-ph-address-book',
};

/**
 * A contagem do prazo fatal dita em palavras — "Vence em 3 dias", "Vence
 * hoje", "Venceu há 2 dias" —, no dia do escritório. O formulário da Agenda e o
 * painel de agendar pela publicação mostram a mesma frase ao lado do campo.
 */
export const frasePrazoFatal = (t, valor, agora = new Date()) => {
  if (!valor) return '';
  const n = diasAteEscritorio(valor, agora);
  if (!Number.isFinite(n)) return '';
  if (n < 0)
    return t('APPOINTMENTS.LEGAL_AGENDA.DETAIL.FATAL_OVERDUE', { n: -n });
  if (n === 0) return t('APPOINTMENTS.LEGAL_AGENDA.DETAIL.FATAL_TODAY');
  if (n === 1) return t('APPOINTMENTS.LEGAL_AGENDA.DETAIL.FATAL_TOMORROW');
  return t('APPOINTMENTS.LEGAL_AGENDA.DETAIL.FATAL_IN', { n });
};
